/* eslint-disable no-unused-vars */
import { T } from "./Translate";
import React, { useState, useEffect } from "react";
import Web3 from "web3";
import { Button } from "@mui/material";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import {
  getDoc,
  doc,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";
import { db } from "../firebase";
import { isLoggedIn } from "../session";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { AppBar, Toolbar, IconButton, Chip } from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "../icons";
import { brandColors, barColors } from "../theme";

function CheckOut() {
    // Web3/Wallet State
    const [walletAddress, setWalletAddress] = useState("");
    const [web3, setWeb3] = useState(null);
    const [ethTxStatus, setEthTxStatus] = useState("");

    // Conectar wallet Ethereum
    const connectWallet = async () => {
      if (window.ethereum) {
        try {
          await window.ethereum.request({ method: "eth_requestAccounts" });
          const web3Instance = new Web3(window.ethereum);
          setWeb3(web3Instance);
          const accounts = await web3Instance.eth.getAccounts();
          setWalletAddress(accounts[0]);
        } catch (error) {
          setEthTxStatus(<T>Error al conectar la wallet: {error.message}</T>);
        }
      } else {
        setEthTxStatus(<T>No se encontró una wallet compatible (MetaMask, Trust Wallet, etc.)</T>);
      }
    };

    // Enviar pago en ETH
    const sendEthPayment = async (amountEth) => {
      if (!web3 || !walletAddress) {
        setEthTxStatus(<T>Conecta tu wallet primero.</T>);
        return;
      }
      try {
        setEthTxStatus(<T>Enviando transacción...</T>);
        const toAddress = "0x000000000000000000000000000000000000dEaD";
        const valueWei = web3.utils.toWei(amountEth.toString(), "ether");
        await web3.eth.sendTransaction({ from: walletAddress, to: toAddress, value: valueWei });
        setEthTxStatus(<T>¡Pago en Ethereum realizado con éxito!</T>);
      } catch (error) {
        setEthTxStatus(<T>Error en el pago: {error.message}</T>);
      }
    };
  const [isContracted, setIsContracted] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [itemDetails, setItemDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { type, itemId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const percentageParam = searchParams.get("percentage");
  const reservationPercentage = Number(percentageParam) || 100;

  const COMMISSION_RATE = 0.05;
  const PLATFORM_PAYPAL_EMAIL = process.env.REACT_APP_PLATFORM_PAYPAL_EMAIL;

  const handleBack = () => {
    navigate(-1);
  };

  const renderHeader = (title) => (
    <AppBar position="fixed" sx={{ bgcolor: barColors.background, color: barColors.text }}>
      <Toolbar sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", width: "100%", maxWidth: { xs: "100%", md: "980px" }, mx: "auto" }}>
          <IconButton edge="start" color="inherit" onClick={handleBack} aria-label={<T>Atrás</T>}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ ml: 1 }}>
            {title}
          </Typography>
        </Box>
      </Toolbar>
    </AppBar>
  );

  const calculatePrices = (basePrice, isQR = false) => {
    const numericPrice = Number(basePrice) || 0;

    if (isQR) {
      const commission = numericPrice * COMMISSION_RATE;
      const total = numericPrice + commission;
      return {
        basePrice: numericPrice.toFixed(2),
        commission: commission.toFixed(2),
        total: total.toFixed(2),
        fullPrice: numericPrice.toFixed(2),
        percentagePaid: 100,
        remainingAmount: "0.00",
      };
    }

    const adjustedBasePrice = numericPrice * (reservationPercentage / 100);
    const commission = adjustedBasePrice * COMMISSION_RATE;
    const total = adjustedBasePrice + commission;

    return {
      basePrice: adjustedBasePrice.toFixed(2),
      commission: commission.toFixed(2),
      total: total.toFixed(2),
      fullPrice: numericPrice.toFixed(2),
      percentagePaid: reservationPercentage,
      remainingAmount: (numericPrice - adjustedBasePrice).toFixed(2),
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = await isLoggedIn();
        if (!user) {
          navigate("/login");
          return;
        }

        const userDocRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
          throw new Error("User document not found");
        }

        setCurrentUser({
          ...user,
          displayName: userDoc.data().displayName,
          profileImageUrl: userDoc.data().profileImageUrl,
        });

        if (location.state?.couponData) {
          const qrData = location.state.couponData;
          setItemDetails({
            type: 'qr',
            price: qrData.totalAmount,
            title: qrData.storeName,
            offers: qrData.offers,
            ownerPaypalEmail: qrData.paypalEmail || PLATFORM_PAYPAL_EMAIL,
            uid: qrData.storeId,
          });
          setLoading(false);
          return;
        }

        const itemCollectionName = type === "auto" ? "cars" : "places";
        const itemRef = doc(db, itemCollectionName, itemId);
        const itemDoc = await getDoc(itemRef);

        if (!itemDoc.exists()) {
          throw new Error("Item not found");
        }

        const itemData = itemDoc.data();
        const itemPrice = Number(itemData.price) || 0;

        const ownerRef = doc(db, "users", itemData.uid);
        const ownerDoc = await getDoc(ownerRef);

        if (!ownerDoc.exists()) {
          throw new Error("Item owner details not found");
        }

        setItemDetails({
          ...itemData,
          price: itemPrice,
          ownerPaypalEmail: ownerDoc.data().paypalEmail,
        });

        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, type, itemId, location.state]);

  const handleQRPayment = async (orderId) => {
    if (!currentUser || !itemDetails) return;

    try {
      await runTransaction(db, async (transaction) => {
        const paymentDocRef = doc(db, "payments", orderId);
        
        const prices = calculatePrices(itemDetails.price, true);

        transaction.set(paymentDocRef, {
          uid: currentUser.uid,
          storeId: itemDetails.uid,
          storeName: itemDetails.title,
          amount: parseFloat(prices.basePrice),
          commission: parseFloat(prices.commission),
          total: parseFloat(prices.total),
          offers: itemDetails.offers,
          paypalOrderId: orderId,
          createdAt: serverTimestamp(),
          type: 'qr_payment',
        });

        const commissionDocRef = doc(db, "commissions", orderId);
        transaction.set(commissionDocRef, {
          orderId,
          storeId: itemDetails.uid,
          buyerId: currentUser.uid,
          baseAmount: parseFloat(prices.basePrice),
          commissionAmount: parseFloat(prices.commission),
          type: 'qr_payment',
          createdAt: serverTimestamp(),
        });
      });

      setIsContracted(true);
      navigate("/home");

      setTimeout(() => {
        alert("¡Pago completado exitosamente! Serás redirigido a tus pagos.");
      }, 1000);
    } catch (error) {
      console.error("Error in payment processing:", error);
      setError(error.message);
    }
  };

  const handleContract = async (orderId) => {
    if (itemDetails.type === 'qr') {
      return handleQRPayment(orderId);
    }

    if (!currentUser || !itemId || !type) return;

    try {
      await runTransaction(db, async (transaction) => {
        const collectionName = type === "auto" ? "vehiculos" : "trips";
        const contractDocRef = doc(
          db,
          collectionName,
          `${currentUser.uid}_${itemId}`
        );

        const contractDoc = await transaction.get(contractDocRef);
        if (contractDoc.exists()) {
          throw new Error("Contract already exists");
        }

        const prices = calculatePrices(itemDetails.price);

        transaction.set(contractDocRef, {
          uid: currentUser.uid,
          sellerId: itemDetails.uid,
          [`${type}Id`]: itemId,
          basePrice: parseFloat(prices.fullPrice),
          paidAmount: parseFloat(prices.basePrice),
          commission: parseFloat(prices.commission),
          totalPrice: parseFloat(prices.total),
          percentagePaid: prices.percentagePaid,
          remainingAmount: parseFloat(prices.remainingAmount),
          paymentCompleted: prices.percentagePaid === 100,
          paypalOrderId: orderId,
          createdAt: serverTimestamp(),
          itemType: type,
          nextPaymentDue:
            prices.percentagePaid < 100
              ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
              : null,
        });

        const itemCollectionName = type === "auto" ? "cars" : "places";
        const itemDocRef = doc(db, itemCollectionName, itemId);
        const itemStatus = {
          vendido: prices.percentagePaid === 100,
          reservado: prices.percentagePaid < 100,
          buyerId: currentUser.uid,
          soldAt: serverTimestamp(),
          percentagePaid: prices.percentagePaid,
          remainingAmount: parseFloat(prices.remainingAmount),
        };
        transaction.update(itemDocRef, itemStatus);

        const commissionDocRef = doc(db, "commissions", orderId);
        transaction.set(commissionDocRef, {
          orderId,
          itemId,
          itemType: type,
          sellerId: itemDetails.uid,
          buyerId: currentUser.uid,
          basePrice: parseFloat(prices.basePrice),
          commissionAmount: parseFloat(prices.commission),
          percentagePaid: prices.percentagePaid,
          createdAt: serverTimestamp(),
        });
      });

      setIsContracted(true);

      if (type === "auto") {
        navigate("/vehicles");
      } else if (type === "viaje") {
        navigate("/my-trips");
      }

      setTimeout(() => {
        const message =
          reservationPercentage === 100
            ? `¡Felicidades por tu ${
                type === "auto" ? "nueva compra" : "reserva"
              }! Pago completado al 100%.`
            : `¡${
                type === "auto" ? "Vehículo reservado" : "Viaje reservado"
              } con un ${reservationPercentage}% de pago inicial!`;

        alert(
          `${message} Serás redirigido a tus ${
            type === "auto" ? "vehículos" : "viajes"
          }.`
        );
      }, 1000);
    } catch (error) {
      console.error("Error in contract creation:", error);
      setError(error.message);
    }
  };

  const createOrder = (data, actions) => {
    if (!itemDetails || typeof itemDetails.price !== "number") {
      throw new Error("Invalid item price");
    }

    const prices = calculatePrices(itemDetails.price, itemDetails.type === 'qr');

    return actions.order.create({
      purchase_units: [
        {
          reference_id: `payment_${itemDetails.type === 'qr' ? 'qr' : itemId}`,
          amount: {
            currency_code: "USD",
            value: prices.basePrice,
          },
          payee: {
            email_address: itemDetails.ownerPaypalEmail,
          },
          description: itemDetails.type === 'qr' 
            ? `Pago en ${itemDetails.title}`
            : `Pago principal por ${type === "auto" ? "auto" : "viaje"}: ${itemDetails.title || itemDetails.description}`,
        },
        {
          reference_id: `commission_${itemDetails.type === 'qr' ? 'qr' : itemId}`,
          amount: {
            currency_code: "USD",
            value: prices.commission,
          },
          payee: {
            email_address: PLATFORM_PAYPAL_EMAIL,
          },
          description: `Comisión de servicio`,
        },
      ],
      application_context: {
        shipping_preference: "NO_SHIPPING",
      },
    });
  };

  const onApprove = (data, actions) => {
    return actions.order.capture().then(function (details) {
      handleContract(details.id);
    });
  };

  if (loading) {
    return (
      <>
        {renderHeader(<T>Checkout</T>)}
        <Container maxWidth="sm" sx={{ pt: 12, pb: 4 }}>
          <Card
            elevation={0}
            sx={{ borderRadius: 3, border: `1px solid ${brandColors.divider}`, backgroundColor: brandColors.backgroundPaper }}
          >
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Button variant="contained" color="secondary" onClick={connectWallet} sx={{ mb: 1, textTransform: "none" }}>
                  {walletAddress ? <><T>Wallet conectada:</T> {walletAddress}</> : <T>Conectar Wallet Ethereum</T>}
                </Button>
                {walletAddress && (
                  <Box display="flex" alignItems="center">
                    <Button variant="contained" color="primary" onClick={() => sendEthPayment(0.01)} sx={{ ml: 2, textTransform: "none" }}>
                      <T>Pagar 0.01 ETH</T>
                    </Button>
                    <Typography variant="caption" sx={{ ml: 1, color: brandColors.textSecondary }}>
                      <T>Este pago cubre la tarifa de transacción y habilita funciones premium.</T>
                    </Typography>
                  </Box>
                )}
                {ethTxStatus && <Alert severity="info" sx={{ mt: 1 }}>{ethTxStatus}</Alert>}
              </Box>
              <Box display="flex" flexDirection="column" alignItems="center" gap={1.5} justifyContent="center" minHeight="200px">
                <CircularProgress sx={{ color: brandColors.accent }} />
                <Typography variant="body2" sx={{ color: brandColors.textSecondary }}>
                  <T>Cargando datos del pago...</T>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </>
    );
  }

  if (!itemDetails || typeof itemDetails.price !== "number") {
    return (
      <>
        {renderHeader(<T>Error</T>)}
        <Container maxWidth="sm" sx={{ pt: 12, pb: 4 }}>
          <Alert severity="error">
            <T>Error: Precio del ítem no válido</T>
          </Alert>
        </Container>
      </>
    );
  }

  const prices = calculatePrices(itemDetails.price, itemDetails.type === 'qr');
  const isQRPayment = itemDetails.type === 'qr';

  const headerTitle = isQRPayment ? (
    <T>Pago QR</T>
  ) : type === "auto" ? (
    reservationPercentage === 100 ? (
      <T>Comprar Auto</T>
    ) : (
      <T>Reservar Auto</T>
    )
  ) : (
    <T>Reservar Viaje</T>
  );

  return (
    <Box sx={{ pb: 7 }}>
      {renderHeader(headerTitle)}

      <Container maxWidth="sm" sx={{ pt: 12, pb: 4 }}>
        <Card
          elevation={0}
          sx={{ borderRadius: 3, border: `1px solid ${brandColors.divider}`, backgroundColor: brandColors.backgroundPaper }}
        >
          <CardContent>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6">
                {isQRPayment ? (
                  <T>Detalles del pago:</T>
                ) : reservationPercentage === 100 ? (
                  <T>Detalles de la compra:</T>
                ) : (
                  <T>Detalles de la reserva:</T>
                )}
              </Typography>
              
              {isQRPayment ? (
                <>
                  <Typography sx={{ mt: 1 }}>
                    <strong><T>Comercio:</T></strong> {itemDetails.title}
                  </Typography>
                  <Divider sx={{ my: 2, borderColor: brandColors.divider }} />

                  {itemDetails.offers?.map((offer, index) => (
                    <Card
                      key={index}
                      elevation={0}
                      sx={{ mt: 2, backgroundColor: brandColors.backgroundDefault, border: `1px solid ${brandColors.divider}`, borderRadius: 2 }}
                    >
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>{offer.title}</Typography>
                        <Typography variant="body2" sx={{ color: brandColors.textSecondary }}>
                          {offer.description}
                        </Typography>
                        <Box
                          sx={{
                            mt: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body2" sx={{ color: brandColors.textSecondary }}>
                            <T>Precio original:</T> ${parseFloat(offer.price).toFixed(2)}
                          </Typography>
                          {offer.offer > 0 && (
                            <Chip
                              size="small"
                              label={`${offer.offer}% OFF`}
                              sx={{ backgroundColor: "success.main", color: "white", fontWeight: "bold" }}
                            />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </>
              ) : (
                <Typography sx={{ mt: 1 }}>
                  {type === "auto" ? <T>Auto:</T> : <T>Destino:</T>}{" "}
                  {itemDetails.title || itemDetails.place}
                </Typography>
              )}

              <Divider sx={{ my: 2, borderColor: brandColors.divider }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography variant="body2" sx={{ color: brandColors.textSecondary }}>
                  <T>Precio total:</T> ${prices.fullPrice}
                </Typography>
                {!isQRPayment && (
                  <Typography variant="body2" sx={{ color: brandColors.textSecondary }}>
                    {type === "auto" ? (
                      <T>Porcentaje de pago inicial:</T>
                    ) : (
                      <T>Porcentaje de reserva:</T>
                    )}{" "}
                    {prices.percentagePaid}%
                  </Typography>
                )}
                <Typography variant="body2" sx={{ color: brandColors.textSecondary }}>
                  <T>Monto a pagar ahora:</T> ${prices.basePrice}
                </Typography>
                <Typography variant="body2" sx={{ color: brandColors.textSecondary }}>
                  <T>Comisión de servicio:</T> ${prices.commission}
                </Typography>
              </Box>

              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  background: `linear-gradient(135deg, ${brandColors.primaryHover} 0%, ${brandColors.primary} 100%)`,
                  color: "white",
                  borderRadius: 2,
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.85, mb: 0.5 }}>
                  <T>Total a pagar ahora</T>
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                  ${prices.total}
                </Typography>
              </Box>

              {!isQRPayment && prices.percentagePaid < 100 && (
                <Box
                  sx={{ mt: 2, p: 2, backgroundColor: brandColors.backgroundDefault, border: `1px solid ${brandColors.divider}`, borderRadius: 2 }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                    <T>Información de pago restante:</T>
                  </Typography>
                  <Typography variant="body2" sx={{ color: brandColors.textSecondary }}>
                    <T>Monto pendiente:</T> ${prices.remainingAmount}
                  </Typography>
                  <Typography variant="body2" sx={{ color: brandColors.textSecondary }}>
                    <T>Fecha límite de pago:</T>{" "}
                    {new Date(
                      Date.now() + 30 * 24 * 60 * 60 * 1000
                    ).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
            </Box>

            {!isContracted && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <PayPalScriptProvider
                  options={{
                    "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
                  }}
                >
                  <PayPalButtons
                    createOrder={createOrder}
                    onApprove={onApprove}
                    style={{
                      layout: "vertical",
                      color: "blue",
                      shape: "rect",
                      label: "pay",
                      height: 45,
                    }}
                  />
                </PayPalScriptProvider>
              </Box>
            )}

            {isContracted && (
              <Alert severity="success">
                {isQRPayment ? (
                  <T>¡Pago realizado con éxito!</T>
                ) : type === "auto" ? (
                  <T>¡Felicidades por tu nueva compra!</T>
                ) : (
                  <T>¡Tu reserva ha sido confirmada!</T>
                )}
              </Alert>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export default CheckOut;